/**
 * API functions for virtual portfolio certificate assignment operations.
 * Matches backend endpoints from admin_virtual_portfolio_controller.py
 */

import { goSolidApi } from "@/composables/useGoSolidApi";

/**
 * Information about matched certificates for a receive order.
 */
export interface MatchedCertificateInfo {
  certificate_ids: number[];
  total_mwh: number;
  registry_name: string | null;
  region: string | null;
  technology: string | null;
  production_period_start: string | null;
  production_period_end: string | null;
}

/**
 * Response structure for a single match.
 */
export interface MatchResponse {
  order_id: number;
  company_id: number;
  company_name: string;
  order_mwh: number;
  created_at: string;
  matched_certificates: MatchedCertificateInfo;
  confidence: string;
  /**
   * When this suggestion was computed. Echoed back on confirm so a suggestion
   * recomputed after display cannot be confirmed unseen.
   */
  matched_at?: string | null;
  /** How the match was found: one registry pickup, or several combined. */
  strategy?: string | null;
  /**
   * The registry pickups the matched certificates arrived in. Optional because
   * matches suggested before this field existed have no arrival information.
   */
  arrival_times?: string[];
}

/**
 * A pending order with no confirmable match yet, and how much volume exists for it.
 * Certificates arrive on a schedule, so an order is often only partly covered.
 */
export interface PendingOrderCoverage {
  order_id: number;
  company_id: number;
  company_name: string;
  order_mwh: number;
  created_at: string;
  available_mwh: number;
  region: string | null;
  /** Enough volume exists to match exactly; a rematch turns it into a suggestion. */
  ready_to_match?: boolean;
}

/**
 * Result of recomputing suggested matches on demand.
 */
export interface RematchResponse {
  matched_orders: number;
  matched_mwh: number;
  still_filling_orders: number;
}

/**
 * Response structure for list of matches.
 */
export interface MatchListResponse {
  matches: MatchResponse[];
  pending_coverage: PendingOrderCoverage[];
}

/**
 * Detailed information about an unknown certificate.
 */
export interface UnknownCertificateResponse {
  id: number;
  registry_name: string | null;
  region: string | null;
  technology: string | null;
  volume_mwh: number;
  production_period_start: string;
  production_period_end: string;
  imported_at: string;
  bundle_start: string | null;
  bundle_end: string | null;
  production_device_name: string | null;
}

/**
 * Response structure for list of unknown certificates.
 */
export interface UnknownCertificateListResponse {
  certificates: UnknownCertificateResponse[];
  total_count: number;
}

/**
 * Request payload to assign certificates to a company.
 */
export interface AssignRequest {
  certificate_ids: number[];
  target_company_id: number;
}

/**
 * Response from assignment operation.
 */
export interface AssignResponse {
  assigned_count: number;
  portfolio_ids: number[];
  connection_ids: number[];
  message: string;
}

/**
 * Request payload to confirm a heuristic match.
 */
export interface ConfirmMatchRequest {
  order_id: number;
  /** The matched_at of the suggestion as displayed; confirm fails if it changed since. */
  matched_at?: string | null;
}

/**
 * Request payload to reject a heuristic match.
 */
export interface RejectMatchRequest {
  order_id: number;
}

export const virtualPortfoliosApi = {
  /**
   * Get pending RECEIVE orders with heuristic matches.
   *
   * @returns Promise with list of matches
   */
  getMatches: () => {
    return goSolidApi.get<MatchListResponse>(
      "/api/admin/virtual-accounts/matches"
    );
  },

  /**
   * Recompute suggested matches now instead of waiting for the next registry sync.
   *
   * @returns Promise with how many orders were matched and how many are still filling
   */
  rematch: () => {
    return goSolidApi.post<RematchResponse>(
      "/api/admin/virtual-accounts/rematch"
    );
  },

  /**
   * Get certificates where company_id is NULL (UNKNOWN certificates).
   *
   * @returns Promise with list of unknown certificates
   */
  getUnknownCertificates: () => {
    return goSolidApi.get<UnknownCertificateListResponse>(
      "/api/admin/virtual-accounts/unknown-certificates"
    );
  },

  /**
   * Manually assign UNKNOWN certificates to a company.
   *
   * @param data - The assignment request containing certificate IDs and target company ID
   * @returns Promise with assignment result
   */
  assignCertificates: (data: AssignRequest) => {
    return goSolidApi.post<AssignResponse>(
      "/api/admin/virtual-accounts/assign",
      data
    );
  },

  /**
   * Confirm a heuristic match, assign certificates, and complete the order.
   *
   * @param data - The request containing the order ID to confirm
   * @returns Promise with confirmation result
   */
  confirmMatch: (data: ConfirmMatchRequest) => {
    return goSolidApi.post<{
      message: string;
      order_id: number;
      status: string;
    }>("/api/admin/virtual-accounts/confirm-match", data);
  },

  /**
   * Reject a heuristic match by removing it from the order.
   *
   * @param data - The request containing the order ID to reject
   * @returns Promise with rejection result
   */
  rejectMatch: (data: RejectMatchRequest) => {
    return goSolidApi.post<{
      message: string;
      order_id: number;
      status: string;
    }>("/api/admin/virtual-accounts/reject-match", data);
  },
};
